import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { I18NextPipe } from 'angular-i18next';
import { MbsValidators, ModalService, ModalSettings, SortEvent, TableColumn } from 'mbs-ui-kit';
import { AdministratorsFacade } from 'mbs-ui-shared/src/lib/facades/administrators.facade';
import AdministratorInCamelCase from 'mbs-ui-shared/src/lib/models/AdministratorInCamelCase';
import { NotificationPlanRule } from 'mbs-ui-shared/src/lib/models/Notification';
import { OsType } from 'mbs-ui-shared/src/lib/modules/schedule-group-action/components/select-action/select-action.component';
import { ScriptLibraryEntry } from 'mbs-ui-shared/src/lib/store/rmmScriptLibrary/model';
import {
  CustomAlertingSettings,
  PorfileRecipientTypes,
  PostAction,
  ProfilePostActions,
  RMMAlertTypes
} from 'mbs-ui-shared@models/rmm-custom-notification-model';
import * as ScriptLibrarySelectors from 'mbs-ui-shared@store/rmmScriptLibrary/selectors';
import { BehaviorSubject, map, noop, switchMap } from 'rxjs';
import { LoadRmmCustomNotificationWizardService } from '../../../../utility/load-rmm-custom-notification-wizard.service';

@UntilDestroy()
@Component({
  selector: 'mbs-custom-profile-notification-utility',
  templateUrl: './custom-profile-notification-utility.component.html'
})
export class CustomProfileNotificationUtility implements OnInit, OnChanges {
  @ViewChild('specifiedEmailModal') specifiedEmailModal: TemplateRef<any>;
  @ViewChild('adminSelectionPushModal') adminSelectionPushModal: TemplateRef<any>;
  @Input() notificationType: string = null;
  @Input() postActions: ProfilePostActions = null;
  @Input() includeConnectWise = false;
  @Output() onPostActionChanged = new EventEmitter<any>();

  public emailValidator = (control: AbstractControl) => (control.value ? MbsValidators.emailValidator(control) : null);
  public orderBy: BehaviorSubject<SortEvent> = new BehaviorSubject({ column: 'email', direction: 'asc' });

  public headers: TableColumn[] = [
    {
      name: this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.secondStep.columnName'),
      sort: 'email',
      gridColSize: '50fr',
      gridColMin: '180px'
    }
  ];

  public pushSettings: {
    type: number;
    users: string[];
  } = {
    type: 0,
    users: []
  };

  public adminList: AdministratorInCamelCase[] = [];
  public selectedAdminList: AdministratorInCamelCase[] = [];
  public providerEmail = '';
  public emailCountLabelKey = 'mbs-ui:rmm-custom-alerting.wizard.secondStep.emailLabel';
  public emailCountLabel = this.i18n.transform(this.emailCountLabelKey, { value: '' });
  public pushAdminCountLabel = this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.secondStep.adminLabel');
  public pushAdminNameList = '';
  public editData = [];
  public keysWithNoScripts: string[] = [RMMAlertTypes.SnmpDevice, RMMAlertTypes.ConnectionLoss];
  public enableScript = true;
  public postActionsTooltip = this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.postActionsTooltip');

  public postActionForm: UntypedFormGroup = new UntypedFormGroup({
    includeProvider: new FormControl(false),
    enableEmails: new FormControl(false),
    emails: new FormControl([]),
    enablePush: new FormControl(false),
    pushAdminIdList: new FormControl([]),
    connectWiseIntegration: new FormControl(false),
    winScript: new FormControl(null),
    linuxScript: new FormControl(null),
    macScript: new FormControl(null)
  });

  public scripts: ScriptLibraryEntry[] = [];
  public postActionScriptList: { id: number; osType: OsType; osList: OsType[]; scriptId?: string }[] = [
    {
      id: 0,
      osType: OsType.Windows,
      osList: [OsType.Windows, OsType.Linux, OsType.MacOS]
    }
  ];
  public osType = OsType;
  public maxHeight = '500px';

  private readonly osList = [OsType.Windows, OsType.Linux, OsType.MacOS];
  private readonly commonKey = 'general';
  private adminListLoaded = false;

  constructor(
    private modalService: ModalService,
    private adminFacade: AdministratorsFacade,
    private i18n: I18NextPipe,
    private store: Store,
    private loadRmmCustomNotificationWizardService: LoadRmmCustomNotificationWizardService
  ) {
    this.loadRmmCustomNotificationWizardService.providerEmail.pipe(untilDestroyed(this)).subscribe((value) => (this.providerEmail = value));
    this.setupAvailableScripts();
    this.orderBy
      .pipe(
        switchMap(() => this.adminFacade.getDataWhenLoaded$),
        map((adminList) => this.getSortedAdminList(adminList.filter((admin) => admin?.enabled))),
        untilDestroyed(this)
      )
      .subscribe({
        next: (adminList) => {
          this.adminList = adminList;
          this.adminListLoaded = true;
          this.refreshSelectedAdminListOnDataChange();
          if (this.postActions) this.onPostActionAndAdminListLoaded();
        },
        error: () => {
          this.adminListLoaded = true;
          this.adminList = [];
          this.onPostActionAndAdminListLoaded();
        }
      });
  }

  ngOnInit(): void {
    this.postActionForm.valueChanges.pipe(untilDestroyed(this)).subscribe((form) => {
      this.emailCountLabel = this.i18n.transform(this.emailCountLabelKey, {
        value: form?.emails?.length ? ` (${form?.emails?.length})` : ''
      });
      this.editData = form?.email?.length ? [...form.emails] : [];

      this.selectedAdminList = form?.pushAdminIdList?.length
        ? this.adminList.filter((admin) => form?.pushAdminIdList.some((id) => id === admin.id))
        : [];

      this.pushAdminNameList = this.selectedAdminList.map((admin) => admin.email).join(', ');
      this.loadRmmCustomNotificationWizardService.pushAdminNameList = this.pushAdminNameList;

      this.pushAdminCountLabel = this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.secondStep.adminLabel', {
        value: form?.pushAdminIdList?.length ? ` (${form?.pushAdminIdList?.length})` : ''
      });

      this.emitValueChange(form);
    });

    this.postActionForm
      .get('emails')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value.length) this.postActionForm.get('enableEmails').setValue(true);
      });
  }

  private emitValueChange(form: any) {
    const postActionPayload: Partial<
      CustomAlertingSettings & {
        connectWise?: { enabled: boolean };
        windowsScriptGuid: string;
        linuxScriptGuid: string;
        macOsScriptGuid: string;
      }
    > = {
      recipient: {
        type: this.getTypeForEmailRecipient(form?.emails ?? [], form?.includeProvider, form?.enableEmails),
        emails: form?.emails?.length && form?.enableEmails ? form?.emails.filter((email) => email !== this.providerEmail) : []
      },
      pushNotification: { enabled: !!form?.enablePush },
      pushRecipient: {
        type: this.getTypeForPushRecipient(form?.pushAdminIdList, form?.enablePush),
        users: form?.pushAdminIdList?.length && form?.enablePush ? [...form.pushAdminIdList] : []
      },
      windowsScriptGuid: this.enableScript ? form?.winScript : null,
      linuxScriptGuid: this.enableScript ? form?.linuxScript : null,
      macOsScriptGuid: this.enableScript ? form?.macScript : null
    };

    postActionPayload.connectWise = { enabled: !!form?.connectWiseIntegration };

    this.onPostActionChanged.emit({ postActionPayload, type: this.notificationType });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.enableScript = !this.keysWithNoScripts.includes(this.notificationType);
    if (this.postActions) this.onPostActionAndAdminListLoaded();
  }

  private onPostActionAndAdminListLoaded() {
    this.loadData(
      this.commonKey in this.postActions && this.postActions[this.commonKey]?.recipient && !this.notificationType
        ? this.postActions[this.commonKey]
        : this.postActions[this.notificationType] ?? {
            ...this.postActions[this.commonKey],
            connectWise: { enabled: false },
            windowsScriptGuid: null,
            linuxScriptGuid: null,
            macOsScriptGuid: null
          }
    );
  }

  loadData(data: PostAction) {
    if (!data || !this.adminListLoaded) return;

    const pushAdmins = this.adminList.filter((admin) => data?.pushRecipient?.users?.includes(admin.id));

    if (
      [
        PorfileRecipientTypes.Provider,
        PorfileRecipientTypes.ProviderAndEmails,
        NotificationPlanRule.Provider,
        NotificationPlanRule.Provider ^ NotificationPlanRule.SpecifiedEmails
      ].includes(data?.pushRecipient?.type) &&
      pushAdmins.every((admin) => !admin.isProvider)
    ) {
      const provider = this.adminList.filter((admin) => {
        return admin.isProvider;
      });

      if (provider.length) pushAdmins.push(...provider);
    }

    this.pushAdminNameList = pushAdmins.map((admin) => admin.email).join(', ');
    this.loadRmmCustomNotificationWizardService.pushAdminNameList = this.pushAdminNameList;
    this.selectedAdminList = [...pushAdmins];

    const formData = {
      includeProvider: [
        PorfileRecipientTypes.Provider,
        PorfileRecipientTypes.ProviderAndEmails,
        NotificationPlanRule.Provider,
        NotificationPlanRule.Provider ^ NotificationPlanRule.SpecifiedEmails
      ].includes(data?.recipient?.type as any),
      enableEmails: [
        NotificationPlanRule.SpecifiedEmails,
        NotificationPlanRule.Administrators,
        NotificationPlanRule.Users,
        NotificationPlanRule.Provider ^ NotificationPlanRule.SpecifiedEmails,
        PorfileRecipientTypes.Administrators,
        PorfileRecipientTypes.SpecifiedEmails,
        PorfileRecipientTypes.Users,
        PorfileRecipientTypes.ProviderAndEmails
      ].includes(data?.recipient?.type as any),
      emails: data?.recipient?.emails ?? [],
      enablePush: !!data?.pushNotification?.enabled && pushAdmins,
      pushAdminIdList: pushAdmins.map((admin) => admin.id),
      connectWiseIntegration: data?.connectWise?.enabled ?? false,
      winScript: data?.windowsScriptGuid ?? null,
      linuxScript: data?.linuxScriptGuid ?? null,
      macScript: data?.macOsScriptGuid ?? null
    };

    if (data?.windowsScriptGuid || data?.linuxScriptGuid || data?.macOsScriptGuid) {
      let i = 0;
      this.postActionScriptList = [];

      if (data?.windowsScriptGuid)
        this.postActionScriptList.push({
          id: i++,
          osType: OsType.Windows,
          osList: [OsType.Windows],
          scriptId: data?.windowsScriptGuid
        });

      if (data?.linuxScriptGuid)
        this.postActionScriptList.push({
          id: i++,
          osType: OsType.Linux,
          osList: [OsType.Linux],
          scriptId: data?.linuxScriptGuid
        });
      if (data?.macOsScriptGuid)
        this.postActionScriptList.push({
          id: i++,
          osType: OsType.MacOS,
          osList: [OsType.MacOS],
          scriptId: data?.macOsScriptGuid
        });
    }

    this.postActionForm.patchValue(formData, { emitEvent: false });
    this.emailCountLabel = this.i18n.transform(this.emailCountLabelKey, {
      value: formData?.emails?.length ? ` (${formData?.emails?.length})` : ''
    });
  }

  getTypeForEmailRecipient(emails: string[], includeProvider: boolean, enableEmails: boolean) {
    const providerIncluded = emails.some((email) => email === this.providerEmail);
    const specifiedEmailList = emails.filter((email) => email !== this.providerEmail);

    if (!includeProvider && !enableEmails) return NotificationPlanRule.None;

    if (!includeProvider && enableEmails && !providerIncluded && !specifiedEmailList.length) return NotificationPlanRule.None;

    if (includeProvider && enableEmails && !specifiedEmailList.length) return NotificationPlanRule.Provider;

    if ((includeProvider && !enableEmails) || (providerIncluded && enableEmails && !specifiedEmailList.length))
      return NotificationPlanRule.Provider;

    if (specifiedEmailList.length && includeProvider && enableEmails)
      return NotificationPlanRule.Provider ^ NotificationPlanRule.SpecifiedEmails;

    return !includeProvider && enableEmails && specifiedEmailList.length ? NotificationPlanRule.SpecifiedEmails : NotificationPlanRule.None;
  }

  getTypeForPushRecipient(selectedList: AdministratorInCamelCase[], enablePush: boolean): NotificationPlanRule {
    if (!selectedList?.length) return NotificationPlanRule.None;

    const providerIncluded = selectedList.some((admin) => admin.isProvider);
    if (providerIncluded && selectedList.length === 1) return NotificationPlanRule.Provider;

    if (!providerIncluded) return NotificationPlanRule.Administrators;

    return enablePush ? NotificationPlanRule.SpecifiedEmails : NotificationPlanRule.None;
  }

  openRecipientModal(): void {
    const settings: ModalSettings = {
      header: { title: this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.secondStep.emailModalTitle'), showCloseCross: false },
      footer: { okButton: { text: this.i18n.transform('shared:buttons.apply') } }
    };

    this.editData = [...this.postActionForm.get('emails').value];

    this.modalService
      .open(settings, this.specifiedEmailModal)
      .then(() => {
        this.postActionForm.get('emails').setValue([...this.editData]);
      })
      .finally(noop);
  }

  openPushModal(): void {
    const settings: ModalSettings = {
      header: { title: this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.secondStep.adminModalTitle'), showCloseCross: false },
      footer: { okButton: { text: this.i18n.transform('shared:buttons.save') } }
    };
    this.modalService
      .open(settings, this.adminSelectionPushModal)
      .then(() => {
        this.postActionForm.get('pushAdminIdList').setValue([...this.selectedAdminList.map((admin) => admin.id)]);
      })
      .finally(noop);
  }

  onSort(event: SortEvent): void {
    this.orderBy.next(event);
  }

  getSortedAdminList(adminList: AdministratorInCamelCase[]): AdministratorInCamelCase[] {
    const sort = this.orderBy.getValue();

    switch (sort.direction) {
      case 'asc':
        return adminList.slice().sort((a, b) => (a[sort.column] > b[sort.column] ? 1 : -1));
      case 'desc':
        return adminList.slice().sort((a, b) => (a[sort.column] < b[sort.column] ? 1 : -1));
      default:
        return adminList.slice();
    }
  }

  private refreshSelectedAdminListOnDataChange(): void {
    if (this.pushSettings?.users.length)
      this.selectedAdminList = this.adminList?.filter((admin) => this.pushSettings.users.includes(admin.id));
  }

  changeForPushSelectedHandler(event: AdministratorInCamelCase[]): void {
    this.selectedAdminList = event;
  }

  getType(): NotificationPlanRule {
    if (!this.selectedAdminList.length) return NotificationPlanRule.None;

    const providerIncluded = this.selectedAdminList.some((admin) => admin.isProvider);
    if (providerIncluded && this.selectedAdminList.length === 1) return NotificationPlanRule.Provider;

    if (!providerIncluded) return NotificationPlanRule.Administrators;

    return NotificationPlanRule.Provider ^ NotificationPlanRule.SpecifiedEmails;
  }

  setupAvailableScripts() {
    this.store
      .select(ScriptLibrarySelectors.selectAll)
      .pipe(untilDestroyed(this))
      .subscribe((scripts) => {
        this.scripts = [...scripts];
      });
  }

  getScriptDescriptionByOsType(osType: OsType) {
    if (!osType) return '';

    const formKey = this.getFormScriptKey(osType);
    if (!this.postActionForm.get(formKey).value) return '';

    return this.scripts.find((script) => script.id === this.postActionForm.get(formKey).value)?.description;
  }

  addScript() {
    const osList = this.osList.filter((osItem) => !this.postActionScriptList.some((item) => item.osType === osItem));
    const usedOs = osList[0];

    this.postActionScriptList.push({
      id: this.postActionScriptList.length,
      osType: usedOs,
      osList
    });

    this.postActionScriptList = this.postActionScriptList.map((item) => ({
      ...item,
      osList: item.osList.filter((os) => os !== usedOs || item.osType === usedOs)
    }));
  }

  updateOsList() {
    const usedOsList = this.postActionScriptList.map((item) => item.osType);
    this.postActionScriptList = this.postActionScriptList.map((item, index) => ({
      ...item,
      id: index,
      osList: this.osList.filter((os) => os === item.osType || !usedOsList.includes(os))
    }));
  }

  onScriptDataChanged(data: any) {
    const formKey = this.getFormScriptKey(data.osType);
    if (!data.enabled) {
      this.postActionScriptList = this.postActionScriptList.filter((item) => item.id !== data.id);
      this.postActionScriptList = this.postActionScriptList.map((item) => {
        if (data.osType) item.osList.push(data.osType);
        return item;
      });

      if (formKey) this.postActionForm.get(formKey).setValue(null);
      this.updateOsList();
      return;
    }

    if (formKey) {
      this.postActionScriptList[data.id].osType = data?.osType;
      this.postActionForm.get(formKey).setValue(data.scriptId);
    }
  }

  getFormScriptKey(osType: OsType) {
    if (osType === OsType.Windows) return 'winScript';
    if (osType === OsType.Linux) return 'linuxScript';
    if (osType === OsType.MacOS) return 'macScript';

    return null;
  }

  trackByOsType(_, item) {
    return item.osType;
  }
}
